function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = "__";

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = []
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          "form",
          `${element.form.getAttribute("id")}-${uniqueKey}`
        );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute(
    "aria-expanded",
    summary.parentNode.hasAttribute("open")
  );

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open")
    );
  });

  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*"
    );
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === "minus"
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle(
        "disabled",
        parseInt(value) <= parseInt(this.input.min)
      );
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options["hideElement"] || province_domid
  );

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this)
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      "button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)"
    ).forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
          event,
          this.mainDetailsToggle.querySelector("summary")
        )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button")
      );
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`
        );
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
              "transitionend",
              addTrapFocus
            );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`
    );
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent)
      elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary")
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset =
      this.borderOffset ||
      this.closest(".header-wrapper").classList.contains(
        "header-wrapper--border-bottom"
      )
        ? 1
        : 0;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(
        this.header.getBoundingClientRect().bottom - this.borderOffset
      )}px`
    );
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(
          this.header.getBoundingClientRect().bottom - this.borderOffset
        )}px`
      );
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight}px`
    );
  };
}

customElements.define("header-drawer", HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      "click",
      this.hide.bind(this, false)
    );
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        )
          this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === "") {
        const productUrl = this.dataset.url.split("?")[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            const sourceQty = html.querySelector(
              ".quick-order-list-container"
            ).parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(
        `#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`
      )
    );
  }
}

customElements.define("bulk-modal", BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(
        this.querySelector("template").content.firstElementChild.cloneNode(true)
      );

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(
        content.querySelector("video, model-viewer, iframe")
      );
      if (focus) deferredElement.focus();
      if (
        deferredElement.nodeName == "VIDEO" &&
        deferredElement.getAttribute("autoplay")
      ) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0
    );
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft -
      this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
        this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    // desactivar 'disabled' de carruseles

    // if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
    //   this.prevButton.setAttribute('disabled', 'disabled');
    // } else {
    //   this.prevButton.removeAttribute('disabled');
    // }

    // if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
    //   this.nextButton.setAttribute('disabled', 'disabled');
    // } else {
    //   this.nextButton.removeAttribute('disabled');
    // }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll(".slider-counter__link")
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener("click", this.linkToSlide.bind(this))
    );
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      );
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true")
          this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute("data-autoplay") === "true")
      this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
      this.sliderAutoplayButton.addEventListener(
        "click",
        this.autoPlayToggle.bind(this)
      );
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add(
      "slider-counter__link--active"
    );
    this.sliderControlButtons[this.currentPage - 1].setAttribute(
      "aria-current",
      true
    );
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (
      !this.reducedMotion.matches &&
      !this.announcementBarArrowButtonWasClicked
    ) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(
      this.autoRotateSlides.bind(this),
      this.autoplaySpeed
    );
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.playSlideshow
      );
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.pauseSlideshow
      );
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === "next" ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = "announcement-bar-slider--fade-in";
    const animationClassOut = "announcement-bar-slider--fade-out";

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext =
      (button === "next" && !isLastSlide) ||
      (button === "previous" && isFirstSlide);
    const direction = shouldMoveNext ? "next" : "previous";

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) +
          1 -
          this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute("selected"))
        .removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest(".product-form__input")
        .querySelector("[data-selected-value] > .swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          swatchValue
        );
        selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          "unset"
        );
        selectedDropdownSwatchValue.classList.add("swatch--unavailable");
      }

      selectedDropdownSwatchValue.style.setProperty(
        "--swatch-focal-point",
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset"
      );
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        .querySelector("[data-selected-value]");
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked")
    ).map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.define("variant-selects", VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: "0px 0px 400px 0px" }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(
      `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`
    )
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (
          !this.querySelector("slideshow-component") &&
          this.classList.contains("complementary-products")
        ) {
          this.remove();
        }

        if (html.querySelector(".grid__item")) {
          this.classList.add("product-recommendations--loaded");
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener(
      "storefront:signincompleted",
      this.handleStorefrontSignInCompleted.bind(this)
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define("account-icon", AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter(
      (queueElement) => !queue.includes(queueElement)
    );
    const quickBulkElement =
      this.closest("quick-order-list") || this.closest("quick-add-bulk");
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.min_error.replace(
          "[min]",
          event.target.dataset.min
        )
      );
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.max_error.replace(
          "[max]",
          event.target.max
        )
      );
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.step_error.replace(
          "[step]",
          event.target.step
        )
      );
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }
}

if (!customElements.get("bulk-add")) {
  customElements.define("bulk-add", BulkAdd);
}

/* Pinflag */

// Añade variante de bolsa al carro --------------------------------------------------------------------------------
function addToCartRecommended(button) {
  const span = button.querySelector(".cart-recommendations-text-button");
  if (span) {
    span.textContent = "añadiendo...";
  }
  const cartItem = button.closest(".cart-recommendations-cart-item ");

  const variantInput = cartItem.getElementsByClassName(
    "cart-recommendations-form-hidden"
  );
  const variantId = variantInput ? variantInput[0].value : null;

  if (variantId) {
    axios
      .post("/cart/add.js", {
        id: variantId,
        quantity: 1,
      })
      .then((response) => {
        console.log("Producto agregado al carrito:", response);
        span.textContent = "agregar al carro";
        setTimeout(() => refreshCartDrawer(), 1000);
      })
      .catch((error) => {
        console.error("Error al agregar el producto al carrito:", error);
      });
  } else {
    console.error("No se encontró el variant ID.");
  }
}

//Actualiza visualmente el carro-----------------------------------------------------------------------------------
function refreshCartDrawer() {
  return fetch("/cart")
    .then((response) => response.text())
    .then((cart) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(cart, "text/html");

      const cartItems = doc.querySelector("#cart-items-list");

      if (cartItems) {
        const cartItemList = document.querySelector("#cart-items-list");
        cartItemList.innerHTML = cartItems ? cartItems.innerHTML : "";

        const drawerFooter = doc.querySelector(".drawer__footer");
        const drawerFooterOld = document.querySelector(".drawer__footer");
        drawerFooterOld.innerHTML = drawerFooter ? drawerFooter.innerHTML : "";

        const cartCountBubble = doc.querySelector(".cart-count-bubble");
        const cartCountBubbleOld = document.querySelector(".cart-count-bubble");
        cartCountBubbleOld.innerHTML = cartCountBubble
          ? cartCountBubble.innerHTML
          : "";
      } else {
        const cartEmpty = doc.querySelector("cart-drawer");
        const cartEmptyOld = document.querySelector("cart-drawer");
        cartEmptyOld.innerHTML = cartEmpty ? cartEmpty.innerHTML : "";

        const cartCountBubble = doc.querySelector(".cart-count-bubble");
        const cartCountBubbleOld = document.querySelector(".cart-count-bubble");
        cartCountBubbleOld.innerHTML = cartCountBubble
          ? cartCountBubble.innerHTML
          : "";
      }
    })
    .catch((error) => console.error("Error updating cart drawer:", error));
}

document.querySelectorAll(".cart-recommendations-cart-item").forEach((card) => {
  const selectSizeButton = card.querySelector(".select-size-button");
  const addToCartButton = card.querySelector(
    ".cart-recommendations-button-add"
  );

  const closeButton = card.querySelector(".close-variants-buttons-recommended");
  const variantButtons = card.querySelector(
    ".cart-recommendations-bag-message"
  );

  selectSizeButton.addEventListener("click", () => {
    if (addToCartButton) {
      addToCartButton.setAttribute("style", "display: block !important;");
      selectSizeButton.setAttribute("style", "display: none !important;");
      variantButtons.setAttribute("style", "display: block !important;");
    }
  });

  closeButton.addEventListener("click", () => {
    if (variantButtons) {
      addToCartButton.setAttribute("style", "display: none !important;");
      selectSizeButton.setAttribute("style", "display: block !important;");
      variantButtons.setAttribute("style", "display: none !important;");
    }
  });
});

document.addEventListener("DOMContentLoaded", () => {
  const buttons = document.querySelectorAll(
    ".card-wrapper .product-form__submit"
  );

  buttons.forEach((button) => {
    button.addEventListener("click", () => {
      const drawerInner = document.querySelector(".drawer__inner");

      if (drawerInner) {
        const hasClasses =
          drawerInner.classList.contains("cart-drawer-pinflag-flex-column") &&
          drawerInner.classList.contains("cart-drawer-pinflag-width");

        if (!hasClasses) {
          drawerInner.classList.add(
            "cart-drawer-pinflag-flex-column",
            "cart-drawer-pinflag-width"
          );
          console.log("Clases agregadas al drawer__inner");
        }
      }
    });
  });
});

document.addEventListener("DOMContentLoaded", () => {
  // Crea un observer para detectar cambios en el DOM
  const observer = new MutationObserver(() => {
    // Verifica si el elemento '.cart-recommendations-cart-item' ha aparecido
    const items = document.querySelectorAll(".cart-recommendations-cart-item");

    if (items.length > 0) {
      items.forEach((item) => {
        // Aquí puedes ejecutar la lógica para trabajar con el nuevo item
        const selectSizeButton = item.querySelector(".select-size-button");
        const addToCartButton = item.querySelector(
          ".cart-recommendations-button-add"
        );
        const closeButton = item.querySelector(
          ".close-variants-buttons-recommended"
        );
        const variantButtons = item.querySelector(
          ".cart-recommendations-bag-message"
        );

        if (
          selectSizeButton &&
          addToCartButton &&
          closeButton &&
          variantButtons
        ) {
          selectSizeButton.addEventListener("click", () => {
            if (addToCartButton) {
              addToCartButton.setAttribute(
                "style",
                "display: block !important;"
              );
              selectSizeButton.setAttribute(
                "style",
                "display: none !important;"
              );
              variantButtons.setAttribute(
                "style",
                "display: block !important;"
              );
            }
          });

          closeButton.addEventListener("click", () => {
            if (variantButtons) {
              addToCartButton.setAttribute(
                "style",
                "display: none !important;"
              );
              selectSizeButton.setAttribute(
                "style",
                "display: block !important;"
              );
              variantButtons.setAttribute("style", "display: none !important;");
            }
          });
        }
      });
    }
  });

  // Configura el observer para observar el cuerpo del documento por cambios
  const config = { childList: true, subtree: true };
  observer.observe(document.body, config);
});

// Permite estar observando si es necesario extender o no el width del carro -----------------------------------------------------------------------
document.addEventListener("DOMContentLoaded", function () {
  console.log("🍥Prueba");
  const cartDrawer = document.querySelector("cart-drawer.drawer");
  const cartRecommendations = document.querySelector("cart-recommendations");

  if (!cartDrawer) {
    console.error("El elemento cartDrawer no se encontró en el DOM");
    return;
  }

  if (!cartRecommendations) {
    return;
  }

  function updateCart() {
    console.log("updateCart");
    const drawerInner = document.querySelector(".drawer__inner");

    if (!drawerInner) {
      console.warn("El elemento drawerInner no se encontró en el DOM");
      return;
    }

    if (cartDrawer.classList.contains("is-empty")) {
      console.log("Esta vacio");
      drawerInner.style.cssText += "width: 40rem !important;";
    } else {
      drawerInner.style.cssText += "width: 72rem !important;";
      console.log("No esta vacio");
    }
  }

  const observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (
        mutation.type === "attributes" &&
        mutation.attributeName === "class"
      ) {
        updateCart();
      }
    });
  });

  observer.observe(cartDrawer, { attributes: true });

  updateCart();
});

/* local storage userInformation ---------------------------------------------- */
function formatRut(rut) {
  return rut.replace(/\./g, ""); // Elimina los puntos
}

function checkUserInformation() {
  const storedData = localStorage.getItem("userInformation");

  const userData = JSON.parse(storedData);
  const fullName = userData.names.split(" ");
  const first_name = fullName[0];
  const last_name = fullName.slice(1).join(" ");
  const email = userData.email;
  const rut = formatRut(userData.rut);

  console.log(first_name, last_name, email, rut);

  exponea.update({
    first_name: first_name,
    last_name: last_name,
    rut: rut,
    email: email,
  });

  exponea.identify({
    email_id: email,
  });
}

document.addEventListener("DOMContentLoaded", function () {
  initializeProductCards();
  initializeInfiniteScroll();
  initializeCartDrawer();
});

function initializeProductCards() {
  console.log("Inicializando tarjetas de producto");
  const productCards = document.querySelectorAll(".product-card-wrapper");
  productCards.forEach(initializeProductCard);

  if (window.location.pathname.includes("/search")) {
    productCards.forEach((card) => {
      const defaultProductUrl = card.querySelector(".card__heading a")?.href;
      if (defaultProductUrl) {
        const cleanUrl = defaultProductUrl.split("?")[0];
        updateVariantSelector(card, cleanUrl, true);
      }
    });
  }
}

function initializeProductCard(productCard) {
  const defaultProductUrl = productCard.querySelector(".card__heading a")?.href;
  if (defaultProductUrl) {
    const cleanUrl = defaultProductUrl.split("?")[0];
    updateVariantSelector(productCard, cleanUrl);
  }

  const defaultColorSwatch = productCard.querySelector(
    ".variant_color_swatches.select_variant"
  );
  if (defaultColorSwatch) {
    defaultColorSwatch.classList.add("selected");
    applySelectedEffect(defaultColorSwatch);

    const productImage1 = productCard.querySelector(
      ".card__media .product_image_1"
    );
    const productImage2 = productCard.querySelector(
      ".card__media .product_image_2"
    );

    if (productImage1 && productImage2) {
      if (
        productImage2.src.includes("Liquid error") ||
        productImage2.srcset.includes("Liquid error")
      ) {
        productImage2.src = productImage1.src;
        productImage2.srcset = productImage1.srcset;
      }
    }
  }

  productCard.querySelectorAll(".variant_color_swatches").forEach((swatch) => {
    swatch.addEventListener("click", function (e) {
      e.preventDefault();

      if (!this.classList.contains("selected")) {
        changeProductPLP(this);
      }
    });
  });

  const addToCartForm = productCard.querySelector(".product-form");
  if (addToCartForm) {
    addToCartForm.addEventListener("submit", handleAddToCart);
  }
}

function initializeInfiniteScroll() {
  const infiniteScrollElement = document.querySelector("infinite-scroll");
  if (infiniteScrollElement) {
    infiniteScrollElement.addEventListener("productsLoaded", function (event) {
      const newProducts = event.detail.newProducts;
      newProducts.forEach(initializeProductCard);
    });
  }
}

function initializeCartDrawer() {
  const cartDrawer = document.querySelector("cart-drawer");
  if (cartDrawer) {
    if (!cartDrawer.open) {
      cartDrawer.open = function () {
        this.classList.add("animate", "active");
        document.body.classList.add("overflow-hidden");
      };
    }

    if (!cartDrawer.close) {
      cartDrawer.close = function () {
        this.classList.remove("active");
        document.body.classList.remove("overflow-hidden");
      };
    }

    const overlay = cartDrawer.querySelector("#CartDrawer-Overlay");
    if (overlay) {
      overlay.addEventListener("click", () => cartDrawer.close());
    }
  }
}

function changeProductPLP(element) {
  const parent = element.closest(".product-card-wrapper");

  if (!parent) {
    console.error("No se encontró el contenedor del producto.");
    return;
  }

  const newProductId = parseInt(element.dataset.productId, 10);
  if (newProductId) {
    const cmpBtn = parent.querySelector('[data-comparator-btn]');
    if (cmpBtn) {
      let oldData = {};
      try { oldData = JSON.parse(cmpBtn.dataset.product); } catch (_) {}
      cmpBtn.dataset.product = JSON.stringify({
        ...oldData,
        id:    newProductId,
        title: element.dataset.productName || oldData.title,
        url:   element.dataset.productUrl  || oldData.url,
        image: element.dataset.featureImage || oldData.image,
      });
      document.dispatchEvent(new CustomEvent('comparator:sync'));
    }
  }

  const baseUrl = window.location.origin;
  const featureImageUrl1 = element.dataset.featureImage;
  const featureImageUrl2 = element.dataset.featureImage2;
  const productUrl = element.dataset.productUrl;
  const productName = element.dataset.productName;

  const productImage1 = parent.querySelector(".card__media .product_image_1");
  const productImage2 = parent.querySelector(".card__media .product_image_2");
  const currentInformation = parent.querySelectorAll(".card__heading a");

  if (!productImage1 || !currentInformation.length) {
    console.error("Elementos necesarios no encontrados.");
    return;
  }

  const isValidSecondImage =
    featureImageUrl2 &&
    featureImageUrl2.trim() !== "" &&
    !featureImageUrl2.includes("Liquid error");

  updateProductImages(
    productImage1,
    productImage2,
    featureImageUrl1,
    isValidSecondImage ? featureImageUrl2 : null
  );
  updateProductInformation(currentInformation, productName, productUrl);
  updateSelectedSwatch(parent, element);

  const fullUrl = `${baseUrl}${productUrl}`;
  const cleanUrl = fullUrl.split("?")[0];

  fetch(cleanUrl + ".js")
    .then((response) => response.json())
    .then((product) => {
      updatePriceDisplay(parent, product);
      updateComparatorButton(parent, product);
    })
    .catch((error) => {
      console.error("Error al obtener información del producto:", error);
    });

  updateVariantSelector(parent, cleanUrl, true);
}

function updateComparatorButton(parent, product) {
  const cmpBtn = parent.querySelector('[data-comparator-btn]');
  if (!cmpBtn) return;

  let oldData = {};
  try { oldData = JSON.parse(cmpBtn.dataset.product); } catch (_) {}

  const variant = product.variants.find((v) => v.available) || product.variants[0];

  const sizeIdx = product.options.findIndex((o) =>
    ['talla', 'talle', 'size', 'taille'].some((s) => o.toLowerCase().includes(s))
  );
  const seenSizeLabels = new Set();
  const sizes = sizeIdx !== -1
    ? product.variants
        .filter((v) => v.available)
        .reduce((acc, v) => {
          const label = v[`option${sizeIdx + 1}`];
          if (!seenSizeLabels.has(label)) {
            seenSizeLabels.add(label);
            acc.push({ label, id: v.id });
          }
          return acc;
        }, [])
    : (oldData.fields?.sizes || []);

  const tmp = document.createElement('div');
  tmp.innerHTML = product.description || '';
  const descFull = tmp.textContent.trim();

  cmpBtn.dataset.product = JSON.stringify({
    ...oldData,
    id:              product.id,
    title:           product.title,
    url:             product.url,
    image:           product.featured_image || oldData.image,
    price:           variant ? variant.price           : (oldData.price || 0),
    compare_at_price: variant ? variant.compare_at_price : null,
    fields: {
      ...oldData.fields,
      vendor:          product.vendor,
      sizes:           sizes,
      descripcion_full: descFull || oldData.fields?.descripcion_full,
    },
  });

  document.dispatchEvent(new CustomEvent('comparator:sync'));
}

function updatePriceDisplay(parent, product) {
  const variant =
    product.variants.find((v) => v.available) || product.variants[0];
  if (!variant) return;

  const priceContainer = parent.querySelector(".price");
  const regularPrice = parent.querySelector(".price-item--regular");
  const salePrice = parent.querySelector(".price-item--sale");
  const badge = parent.querySelector(".card__badge");

  if (variant.compare_at_price && variant.compare_at_price > variant.price) {
    priceContainer?.classList.add("price--on-sale");

    if (regularPrice) {
      regularPrice.style.display = "";
      regularPrice.textContent = `$${formatPrice(variant.compare_at_price)}`;
    }

    if (salePrice) {
      salePrice.style.display = "";
      salePrice.textContent = `$${formatPrice(variant.price)}`;
    }

    updateDiscountBadge(badge, variant.price, variant.compare_at_price);
  } else {
    priceContainer?.classList.remove("price--on-sale");

    if (regularPrice) {
      regularPrice.style.display = "";
      regularPrice.textContent = `$${formatPrice(variant.price)}`;
    }

    if (salePrice) {
      salePrice.style.display = "none";
    }

    removeSaleBadge(badge);
  }

  updateSoldOutBadge(badge, variant.available);
}

function updateDiscountBadge(badgeContainer, price, comparePrice) {
  if (!badgeContainer) return;

  const existingSaleBadge = badgeContainer.querySelector(
    ".color-badge-pinflag"
  );
  let colorSchemeClass = "";

  if (existingSaleBadge) {
    const colorClass = Array.from(existingSaleBadge.classList).find(
      (className) =>
        className.startsWith("color-") && className !== "color-badge-pinflag"
    );
    if (colorClass) {
      colorSchemeClass = colorClass;
    }
    existingSaleBadge.remove();
  }

  const discount = comparePrice - price;
  const discountPercentage = Math.round((discount / comparePrice) * 100);

  const saleBadge = document.createElement("span");
  saleBadge.classList.add("badge", "badge--bottom-left", "color-badge-pinflag");

  if (colorSchemeClass) {
    saleBadge.classList.add(colorSchemeClass);
  }

  saleBadge.textContent = `-${discountPercentage}%`;

  badgeContainer.appendChild(saleBadge);
}

function removeSaleBadge(badgeContainer) {
  if (!badgeContainer) return;

  const saleBadge = badgeContainer.querySelector(".color-badge-pinflag");
  if (saleBadge) {
    saleBadge.remove();
  }
}

function updateSoldOutBadge(badgeContainer, isAvailable) {
  if (!badgeContainer) return;

  const soldOutBadge = badgeContainer.querySelector(
    ".badge:not(.color-badge-pinflag)"
  );

  if (!isAvailable) {
    if (!soldOutBadge) {
      const newSoldOutBadge = document.createElement("span");
      newSoldOutBadge.classList.add("badge", "badge--bottom-left");
      newSoldOutBadge.textContent = "Agotado";
      badgeContainer.appendChild(newSoldOutBadge);
    }
  } else if (soldOutBadge) {
    soldOutBadge.remove();
  }
}

function formatPrice(price) {
  return (price / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

function updateProductImages(image1, image2, url1, url2) {
  if (image1) {
    image1.src = url1;
    image1.srcset = url1;
  }

  if (image2) {
    if (url2 && !url2.includes("Liquid error") && url2.trim() !== "") {
      image2.style.display = "";
      image2.src = url2;
      image2.srcset = url2;
    } else {
      image2.style.display = "";
      image2.src = url1;
      image2.srcset = url1;
    }
  }
}

function updateProductInformation(infoElements, name, url) {
  infoElements.forEach((element) => {
    const desktopTitle = element.querySelector(".produt_title_desktop");
    if (desktopTitle) {
      desktopTitle.textContent = name;
    }

    const modelSpan = element.querySelector(".model-span");
    const descriptionSpan = element.querySelector(".plp-description-span");

    if (modelSpan) {
      modelSpan.textContent =
        element
          .closest(".product-card-wrapper")
          .querySelector(".variant_color_swatches.selected")?.dataset
          .productModelo || "";
    }

    if (descriptionSpan) {
      descriptionSpan.textContent =
        element
          .closest(".product-card-wrapper")
          .querySelector(".variant_color_swatches.selected")?.dataset
          .productPlpDescripcion || "";
    }

    element.href = url;
  });
}

function updateSelectedSwatch(parent, selectedElement) {
  parent.querySelectorAll(".variant_color_swatches").forEach((swatch) => {
    swatch.classList.remove("select_variant", "selected");
    const effect = swatch
      .closest(".swatch_item")
      ?.querySelector(".selected-effect");
    if (effect) effect.remove();
  });

  selectedElement.classList.add("select_variant", "selected");
  applySelectedEffect(selectedElement);
}

function applySelectedEffect(element) {
  const parent = element.closest(".swatch_item");
  if (parent) {
    const existingEffect = parent.querySelector(".selected-effect");
    if (existingEffect) existingEffect.remove();

    const afterElement = document.createElement("div");
    afterElement.classList.add("selected-effect");
    parent.appendChild(afterElement);
  }
}

function updateVariantSelector(parent, productUrl, forceUpdate = false) {
  const variantSelector = parent.querySelector(".product-variant-selector");
  const variantButtons = parent.querySelector(".variant-buttons");
  const productForm = parent.querySelector(".product-form");
  const selectedVariantInput = parent.querySelector(".selected-variant-id");
  const submitButton = parent.querySelector(".product-variant-submit-button");
  const soldOutMessage = parent.querySelector(".sold-out-message");

  if (
    !variantSelector ||
    !variantButtons ||
    !productForm ||
    !selectedVariantInput
  ) {
    console.error(
      "Elementos necesarios no encontrados para el selector de variantes."
    );
    return;
  }

  fetch(productUrl + ".js")
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then((product) => {
      const availableVariants = product.variants.filter((v) => v.available);

      if (availableVariants.length === 0) {
        variantSelector.style.display = "none";
        if (soldOutMessage) soldOutMessage.style.display = "flex";
      } else {
        if (soldOutMessage) soldOutMessage.style.display = "none";

        renderVariantButtons(variantButtons, product.variants);

        if (availableVariants.length > 0) {
          selectVariantSilently(parent, availableVariants[0].id);
        }
      }
    })
    .catch((error) => {
      console.error("Error al cargar las variantes:", error);
    });
}

function renderVariantButtons(container, variants) {
  // Función para ordenar tallas integrada
  function sortVariantsBySize(variants) {
    return variants.sort((a, b) => {
      const sizeA = a.title.split("/")[0].trim();
      const sizeB = b.title.split("/")[0].trim();

      const numA = parseInt(sizeA);
      const numB = parseInt(sizeB);

      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }

      const alphabeticalOrder = [
        "XXS",
        "XS",
        "S",
        "M",
        "L",
        "XL",
        "XXL",
        "XXXL",
      ];
      const indexA = alphabeticalOrder.indexOf(sizeA.toUpperCase());
      const indexB = alphabeticalOrder.indexOf(sizeB.toUpperCase());

      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      if (!isNaN(numA) && indexB !== -1) return -1;
      if (!isNaN(numB) && indexA !== -1) return 1;
      if (!isNaN(numA) && isNaN(numB)) return -1;
      if (!isNaN(numB) && isNaN(numA)) return 1;
      if (indexA !== -1 && indexB === -1) return -1;
      if (indexB !== -1 && indexA === -1) return 1;

      return sizeA.localeCompare(sizeB);
    });
  }

  const sortedVariants = sortVariantsBySize([...variants]);

  container.dataset.preOrdered = "true";

  container.innerHTML = "";
  sortedVariants.forEach((variant) => {
    const button = createVariantButton(variant);
    // if(index === 0){
    //   button.classList.add('selected');
    // }
    container.appendChild(button);
  });
}

function createVariantButton(variant) {
  const button = document.createElement("button");
  button.type = "button";
  button.classList.add("variant-button");
  if (!variant.available) {
    button.classList.add("unavailable");
  }
  button.textContent = variant.title.split("/")[0].trim();
  button.dataset.variantId = variant.id;

  button.addEventListener("click", function (e) {
    e.preventDefault();
    if (variant.available) {
      selectVariantWithVisuals(
        this.closest(".product-card-wrapper"),
        variant.id
      );
    }
  });

  return button;
}

function selectVariantSilently(parent, variantId) {
  const selectedVariantInput = parent.querySelector(".selected-variant-id");
  const submitButton = parent.querySelector(".product-form__submit");

  if (selectedVariantInput) selectedVariantInput.value = variantId;
  if (submitButton) {
    submitButton.disabled = false;
    const span = submitButton.querySelector("span");
    if (span) span.textContent = submitButton.dataset.labelAdd || 'agregar al carro';
  }
}

function selectVariantWithVisuals(parent, variantId) {
  const variantButtons = parent.querySelectorAll(".variant-button");
  const selectedVariantInput = parent.querySelector(".selected-variant-id");
  const submitButton = parent.querySelector(".product-form__submit");

  variantButtons.forEach((button) => {
    button.classList.toggle(
      "selected",
      button.dataset.variantId === variantId.toString()
    );
  });

  if (selectedVariantInput) selectedVariantInput.value = variantId;
  if (submitButton) {
    submitButton.disabled = false;
    const span = submitButton.querySelector("span");
    if (span) span.textContent = "agregar al carro";
  }

  getVariantDetails(parent, variantId)
    .then((selectedVariant) => {
      if (selectedVariant) {
        const colorSwatch = parent.querySelector(
          `.variant_color_swatches[data-color="${selectedVariant.option1}"]`
        );
        if (colorSwatch) {
          changeProductPLP(colorSwatch);
        }
      }
    })
    .catch((error) => {
      console.error("Error al seleccionar la variante:", error);
    });
}

function getVariantDetails(parent, variantId) {
  const productUrl = parent.querySelector(".card__heading a")?.href;
  if (!productUrl) return Promise.reject("URL del producto no encontrada");

  const cleanUrl = productUrl.split("?")[0];

  return fetch(cleanUrl + ".js")
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then((product) => {
      return product.variants.find(
        (v) => v.id.toString() === variantId.toString()
      );
    });
}


function updateCartDrawer(openDrawer = false) {
  fetch("/?section_id=cart-drawer")
    .then((response) => response.text())
    .then((html) => {
      const parser = new DOMParser();
      const newDocument = parser.parseFromString(html, "text/html");
      const newDrawerContent = newDocument.querySelector(".drawer__inner");
      const cartDrawer = document.querySelector("cart-drawer");

      if (newDrawerContent && cartDrawer) {
        const currentDrawerContent = cartDrawer.querySelector(".drawer__inner");
        if (currentDrawerContent)
          currentDrawerContent.innerHTML = newDrawerContent.innerHTML;

        cartDrawer.classList.toggle(
          "is-empty",
          newDrawerContent.classList.contains("is-empty")
        );

        if (openDrawer) {
          cartDrawer.open();
        }
      }

      updateCartIconBubble();
    })
    .catch((error) => {
      console.error("Error al actualizar el carrito:", error);
    });
}

function updateCartIconBubble() {
  fetch("/cart.js")
    .then((response) => response.json())
    .then((cart) => {
      const cartIconBubble = document.querySelector("#cart-icon-bubble");
      if (cartIconBubble) {
        const itemCount = cart.item_count;
        const counterElement = cartIconBubble.querySelector(
          ".cart-count-bubble span"
        );

        if (counterElement) {
          counterElement.textContent = itemCount;
          counterElement
            .closest(".cart-count-bubble")
            ?.classList.toggle("hide", itemCount === 0);
        } else {
          const newCounterElement = document.createElement("div");
          newCounterElement.className = "cart-count-bubble";
          newCounterElement.innerHTML = `<span aria-hidden="true">${itemCount}</span><span class="visually-hidden">${itemCount} artículo(s)</span>`;
          cartIconBubble.appendChild(newCounterElement);
        }
      }
    })
    .catch((error) => {
      console.error("Error al actualizar el icono del carrito:", error);
    });
}

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

const debouncedUpdateVariantSelector = debounce(updateVariantSelector, 300);
const debouncedChangeProductPLP = debounce(changeProductPLP, 300);

document.querySelectorAll(".swatch").forEach(function (swatch) {
  swatch.addEventListener(
    "click",
    debounce(function () {
      document.querySelectorAll(".swatch").forEach(function (item) {
        item.classList.remove("swatch-selected");
      });

      this.classList.add("swatch-selected");
    }, 300)
  );
});

document.addEventListener("DOMContentLoaded", function (event) {
  document.querySelectorAll(".color__container").forEach((div) => {
    let isDragging = false,
      startX,
      scrollLeft;

    div.addEventListener("mousedown", (e) => {
      isDragging = true;
      startX = e.pageX - div.offsetLeft;
      scrollLeft = div.scrollLeft;
      div.style.cursor = "grabbing";
    });

    div.addEventListener("mouseleave", () => {
      isDragging = false;
      div.style.cursor = "grab";
    });

    div.addEventListener("mouseup", () => {
      isDragging = false;
      div.style.cursor = "grab";
    });

    div.addEventListener("mousemove", (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const x = e.pageX - div.offsetLeft;
      const walk = (x - startX) * 2;
      div.scrollLeft = scrollLeft - walk;
    });
  });

  const targetNode = document.getElementById("product-grid");

  if (targetNode) {
    const observer = new MutationObserver((mutationsList) => {
      mutationsList.forEach((mutation) => {
        if (mutation.type === "childList") {
          console.log("Se ha modificado el contenido del div:");

          document.querySelectorAll(".color__container").forEach((div) => {
            let isDragging = false,
              startX,
              scrollLeft;

            div.addEventListener("mousedown", (e) => {
              isDragging = true;
              startX = e.pageX - div.offsetLeft;
              scrollLeft = div.scrollLeft;
              div.style.cursor = "grabbing";
            });

            div.addEventListener("mouseleave", () => {
              isDragging = false;
              div.style.cursor = "grab";
            });

            div.addEventListener("mouseup", () => {
              isDragging = false;
              div.style.cursor = "grab";
            });

            div.addEventListener("mousemove", (e) => {
              if (!isDragging) return;
              e.preventDefault();
              const x = e.pageX - div.offsetLeft;
              const walk = (x - startX) * 2;
              div.scrollLeft = scrollLeft - walk;
            });
          });
        }
      });
    });

    observer.observe(targetNode, { childList: true });
  }
});
